import cn from 'classnames'
import { ChangeEvent, ElementType, FC, FocusEvent, TextareaHTMLAttributes, useEffect, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { useIsomorphicLayoutEffect } from 'react-use'

import styles from './EditableTextOld.module.scss'

import Text from '~lib/ui-kit/Text'

export type EditableTextOldProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  as?: ElementType
  active?: boolean
  variant?: 'heading-l' | 'heading-m' | 'heading-s' | 'body'
  error?: string
  onChangeActive?: (active: boolean) => void
}

const EditableTextOld: FC<React.PropsWithChildren<EditableTextOldProps>> = ({
  as: Element = 'p',
  active,
  variant = 'body',
  error,
  className,
  disabled,
  placeholder,
  rows = 1,
  readOnly,
  style,
  value,
  defaultValue,
  onChange,
  onChangeActive,
  onFocus,
  onBlur,
  ...props
}) => {
  const [activeState, setActiveState] = useState(active)
  const [innerValue, setInnerValue] = useState(value || defaultValue)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (typeof active !== 'undefined') {
      setActiveState(active)
    }
  }, [active])

  const changeActive = (value: boolean) => {
    if (onChangeActive) {
      onChangeActive(value)
    } else {
      setActiveState(value)
    }
  }

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    e.persist()
    const value = e.currentTarget.value.replace(/\n/g, '')
    e.currentTarget.value = value
    setInnerValue(value)
    onChange?.(e)
  }

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    e.persist()
    changeActive(false)
    if (onBlur) onBlur(e)
  }

  const handleClickText = () => {
    if (!disabled && !readOnly) {
      changeActive(true)
    }
  }

  useEffect(() => {
    if (typeof value !== undefined) {
      setInnerValue(value)
    }
  }, [value])

  useIsomorphicLayoutEffect(() => {
    if (activeState) {
      const len = textareaRef.current?.value.length
      textareaRef.current?.focus?.()
      textareaRef.current?.setSelectionRange(len, len)
    }
  }, [activeState])

  return (
    <>
      <TextareaAutosize
        ref={textareaRef}
        rows={rows}
        maxRows={1}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        spellCheck={false}
        className={cn(
          styles.root,
          styles[`variant-${variant}`],
          disabled && styles.disabled,
          readOnly && styles.readOnly,
          !activeState && styles.hidden,
          className
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={innerValue}
        {...props}
      />

      {!activeState && (
        <Element
          tabIndex={props.tabIndex}
          onMouseUp={handleClickText}
          className={cn(
            styles.root,
            styles[`variant-${variant}`],
            disabled && styles.disabled,
            readOnly && styles.readOnly,
            !innerValue && styles.placeholder,
            className
          )}
        >
          {innerValue || placeholder}
        </Element>
      )}

      {error && (
        <Text as="div" type="caption" variant="danger" className={styles.error}>
          {error}
        </Text>
      )}
    </>
  )
}

export default EditableTextOld
